/**
 * Veluna Ritual Quiz
 * Multi-step quiz to recommend personalized sleep rituals
 * Optimized for 100M ARR: Smart advance, weighted scoring, 4 questions
 * Version: 2025-12-31-fix-questions-display
 */

(function() {
  'use strict';
  
  // Full-screen quiz takeover: add body class for CSS fallback + JS hide chrome
  (function fullscreenTakeover() {
    document.body.classList.add('veluna-quiz-fullscreen');
    // JS fallback for browsers without :has() support
    var chrome = document.querySelector('[data-veluna-top-chrome]');
    var header = document.getElementById('shopify-section-header');
    var footer = document.getElementById('shopify-section-footer');
    var press = document.querySelector('[id*="veluna-press"]');
    [chrome, header, footer, press].forEach(function(el) {
      if (el) el.style.display = 'none';
    });
    document.body.style.paddingTop = '0';
  })();
  
  // Helper to log diagnostics if available
  function logDiag(category, message, data) {
    if (window.quizDiagnostics) {
      window.quizDiagnostics.log(category, message, data);
    }
  }
  
  function captureState(label) {
    if (window.quizDiagnostics) {
      return window.quizDiagnostics.captureRadioState(label);
    }
  }
  
  // IMMEDIATE: Clear ALL quiz-related storage before anything else
  try {
    sessionStorage.removeItem('veluna_quiz_progress');
    sessionStorage.removeItem('veluna_quiz_answers');
    sessionStorage.removeItem('veluna_quiz_utm_params');
    sessionStorage.removeItem('veluna_quiz_start_time');
    localStorage.removeItem('veluna_quiz_progress');
    localStorage.removeItem('veluna_quiz_answers');
    logDiag('INIT', 'Storage cleared');
  } catch (e) {
    // Storage access might be blocked, ignore
  }
  
  // Detect mobile for aggressive preselection prevention
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  logDiag('INIT', 'Mobile detection', { isMobileDevice });

  // Mobile no-scroll fit: keeps all elements visible; only tightens spacing when needed
  function applyMobileNoScrollFit() {
    try {
      if (!isMobileDevice) return;
      if (!document.body || !document.querySelector('.veluna-section.veluna-quiz')) return;

      const container = document.querySelector('.veluna-quiz__container');
      if (!container) return;

      const viewportHeight = (window.visualViewport && window.visualViewport.height) ? window.visualViewport.height : window.innerHeight;
      const available = Math.max(320, viewportHeight - 16); // keep a small buffer

      // Reset before measuring
      document.body.classList.remove('veluna-quiz-compact', 'veluna-quiz-ultra');

      // Measure current layout height
      const height0 = container.getBoundingClientRect().height;
      if (height0 <= available) return;

      document.body.classList.add('veluna-quiz-compact');

      // Re-measure after styles apply; escalate to ultra if still overflowing
      requestAnimationFrame(() => {
        try {
          const height1 = container.getBoundingClientRect().height;
          if (height1 > available) {
            document.body.classList.add('veluna-quiz-ultra');
          }
        } catch (e) {
          // ignore
        }
      });
    } catch (e) {
      // ignore
    }
  }

  // Re-apply fit on viewport changes (mobile address bar/orientation)
  try {
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', applyMobileNoScrollFit);
      window.visualViewport.addEventListener('scroll', applyMobileNoScrollFit);
    }
    window.addEventListener('orientationchange', function() {
      setTimeout(applyMobileNoScrollFit, 60);
    });
    window.addEventListener('resize', function() {
      setTimeout(applyMobileNoScrollFit, 60);
    });
  } catch (e) {
    // ignore
  }
  
  // Prevent bfcache on mobile (back/forward cache stores form state)
  if (isMobileDevice) {
    window.addEventListener('pageshow', function(e) {
      if (e.persisted) {
        // Page was restored from bfcache, reload to clear form state
        window.location.reload();
      }
    });
    
    // Also prevent pagehide from caching
    window.addEventListener('pagehide', function() {
      // Don't let browser cache form state
    }, true);
    
    // AGGRESSIVE MOBILE FIX: Disable all radios by default to prevent browser form restoration
    const allRadios = document.querySelectorAll('[data-quiz-form] input[type="radio"]');
    allRadios.forEach(function(r) {
      r.disabled = true;
      r.checked = false;
      r.removeAttribute('checked');
    });
    
    let lastMobileClick = 0;
    
    // Track clicks to enable radios temporarily
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-quiz-form]')) {
        lastMobileClick = Date.now();
        // Enable radios for 300ms window to allow click to register
        const quizRadios = document.querySelectorAll('[data-quiz-form] input[type="radio"]');
        quizRadios.forEach(function(r) {
          r.disabled = false;
        });
        
        // Re-disable after window closes
        setTimeout(function() {
          quizRadios.forEach(function(r) {
            r.disabled = true;
            r.checked = false;
          });
        }, 300);
      }
    }, true);
    
    // Also continuously clear any checked radios that browser tries to restore
    setInterval(function() {
      const timeSinceClick = Date.now() - lastMobileClick;
      if (timeSinceClick < 300) {
        return; // In click window, allow selection
      }
      
      const quizRadios = document.querySelectorAll('[data-quiz-form] input[type="radio"]');
      quizRadios.forEach(function(r) {
        if (r.checked) {
          r.checked = false;
          r.removeAttribute('checked');
        }
      });
    }, 100);
  }
  
  // Global state tracking
  let currentRadioName = null; // Track the current dynamic radio name for Previous button
  
  // Question weights for weighted scoring (not simple vote counting)
  const QUESTION_WEIGHTS = {
    'sleep_challenge': 3.0,  // Primary pain point = most important
    'current_routine': 1.5,  // Next-day signal (reinforces ritual match)
    'sleep_schedule': 1.0,   // Category refinement
    'goals': 1.5             // Commitment signal
  };
  
  // Smart advance delay (visual feedback before auto-advance)
  const ADVANCE_DELAY = 250; // milliseconds
  
  // Centralized microcopy for quiz steps (additive UX copy only)
  // DO NOT modify questions, answers, or quiz logic
  const QUIZ_MICROCOPY = {
    // Pre-question helper (appears above question, same for all steps)
    preQuestionHelper: "Each step helps shape your personalized ritual.",
    
    // Step-specific subheadlines (appears below question title)
    subheadlines: {
      'sleep_challenge': "This identifies the primary system that needs support.",
      'current_routine': "This helps us personalize your match.",
      'sleep_schedule': "Timing influences how rituals are structured and sustained.",
      'goals': "Your ritual is designed around the outcome you want to wake up with."
    }
  };

  // ─── INTERSTITIAL SLIDES ─────────────────────────────────────────────────
  // Aspirational/benefits slides shown between questions to boost engagement
  const INTERSTITIAL_SLIDES = [
    {
      afterStep: 0, // Show after Q1 (sleep_quality)
      slides: [
        {
          id: 'aspirational-1',
          headline: 'Better sleep changes everything',
          benefits: [
            'Wake up with natural energy',
            'Sharper focus and clearer thinking all day',
            'Visibly calmer skin and a refreshed glow'
          ],
          image: '{{ "quiz-interstitial-sleep.png" | asset_url }}',
          imageFallback: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/t/706/assets/quiz-interstitial-sleep.png',
          cta: 'Continue'
        }
      ]
    },
    {
      afterStep: 2, // Show after Q3 (sleep_schedule)
      slides: [
        {
          id: 'aspirational-2',
          headline: 'A ritual built around your life',
          subtext: 'Based on how you sleep, what you need, and where you want to be. We\'re crafting something personal.',
          image: '{{ "quiz-interstitial-cozy.png" | asset_url }}',
          imageFallback: 'https://cdn.shopify.com/s/files/1/0660/1372/4750/t/706/assets/quiz-interstitial-cozy.png',
          cta: 'Continue'
        }
      ]
    }
  ];

  // Track interstitial state
  let pendingInterstitials = [];  // Queue of slides yet to show
  let interstitialActive = false; // Whether an interstitial is currently visible
  
  // OPTIMAL UX: Redirect to My Ritual page (not quiz-results)
  // My Ritual page is the canonical destination for quiz results
  const DEFAULT_RESULTS_HANDLE = 'my-ritual';
  
  function getQuizResultsHandle() {
    // Check for override in theme settings
    const handle = window.VelunaThemeSettings && window.VelunaThemeSettings.quizResultsHandle
      ? window.VelunaThemeSettings.quizResultsHandle
      : DEFAULT_RESULTS_HANDLE;
    return handle.replace(/^\/+|\/+$/g, '');
  }
  
  function buildQuizResultsUrl(category, bundleType) {
    // Check if user came from My Ritual redirect
    const urlParams = new URLSearchParams(window.location.search);
    const redirectParam = urlParams.get('redirect');
    const cameFromMyRitual = redirectParam === 'my-ritual';
    
    // Use My Ritual URL helper if available, otherwise build manually
    if (window.VelunaQuizRouting && typeof window.VelunaQuizRouting.getMyRitualUrl === 'function') {
      const baseUrl = window.VelunaQuizRouting.getMyRitualUrl();
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (bundleType) params.set('bundle', bundleType);
      params.set('from_quiz', 'true'); // Mark that user came from quiz
      if (cameFromMyRitual) {
        params.set('return_to_ritual', 'true'); // Mark return from redirect
      }
      const previewThemeId = urlParams.get('preview_theme_id');
      if (previewThemeId) params.set('preview_theme_id', previewThemeId);
      const queryString = params.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }
    
    // Fallback: Build URL manually
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (bundleType) params.set('bundle', bundleType);
    params.set('from_quiz', 'true'); // Mark that user came from quiz
    if (cameFromMyRitual) {
      params.set('return_to_ritual', 'true'); // Mark return from redirect
    }
    const previewThemeId = urlParams.get('preview_theme_id');
    if (previewThemeId) params.set('preview_theme_id', previewThemeId);
    const queryString = params.toString();
    return queryString
      ? `/pages/${getQuizResultsHandle()}?${queryString}`
      : `/pages/${getQuizResultsHandle()}`;
  }
  
  // 4 questions optimized based on deep product analysis
  // Each question maps directly to unique value propositions from product PDFs
  const QUIZ_QUESTIONS = [
    {
      id: 'sleep_challenge',
      question: "What's your biggest sleep challenge?",
      type: 'single',
      options: [
        { value: 'racing_mind', label: 'My mind won\'t shut off - racing thoughts and anxiety', category: 'mind-calm' },
        { value: 'physical_tension', label: 'Physical tension and restlessness - my body won\'t relax', category: 'deep-restore' },
        { value: 'irregular_schedule', label: 'Sleep timing - I can\'t fall asleep or wake up when I want', category: 'circadian-reset' },
        { value: 'visible_stress', label: 'Visible signs of stress - tired face, dull skin, lack of radiance', category: 'sacred-soothe' }
      ]
    },
    {
      id: 'current_routine',
      question: "When your sleep is off, what do you feel most the next day?",
      type: 'single',
      options: [
        { value: 'mental_drained', label: 'My mind feels busy or anxious, mentally drained', category: 'mind-calm' },
        { value: 'body_heavy', label: 'My body feels heavy, sore, or not recovered', category: 'deep-restore' },
        { value: 'out_of_sync', label: 'My energy is out of sync, wired late or crashing at the wrong time', category: 'circadian-reset' },
        { value: 'look_tired', label: 'I look tired, dull skin, puffiness, or low radiance', category: 'sacred-soothe' }
      ]
    },
    {
      id: 'sleep_schedule',
      question: "What's your sleep schedule?",
      type: 'single',
      options: [
        { value: 'regular', label: 'Regular schedule (consistent bedtime)', category: null },
        { value: 'irregular', label: 'My sleep timing doesn\'t match what I want (night owl, early bird, or misaligned)', category: 'circadian-reset' },
        { value: 'doesnt_matter', label: 'Schedule doesn\'t matter - my challenge is mental/physical/beauty-related', category: null },
        { value: 'beauty_focus', label: 'I prioritize beauty sleep and visible results', category: 'sacred-soothe' }
      ]
    },
    {
      id: 'goals',
      question: "What's your main sleep goal?",
      type: 'single',
      options: [
        { value: 'quiet_mind', label: 'Quiet my racing mind and eliminate sleep anxiety', category: 'mind-calm' },
        { value: 'physical_recovery', label: 'Optimize physical recovery and muscle repair', category: 'deep-restore' },
        { value: 'reset_clock', label: 'Master my sleep timing - fall asleep and wake up when I want', category: 'circadian-reset' },
        { value: 'radiant_skin', label: 'Wake up with visible glow and radiant skin', category: 'sacred-soothe' }
      ]
    }
  ];
  
  let currentStep = 0;
  let answers = {};
  let isAdvancing = false; // Prevent double-advance
  let advanceTimeout = null; // Track pending advance timeout
  let isResuming = false; // Track if we're resuming from saved progress
  
  /**
   * Initialize quiz
   */
  function initQuiz() {
    console.log('Quiz: Initializing...');
    const form = document.querySelector('[data-quiz-form]');
    if (!form) {
      console.error('Quiz: Form element not found! Looking for [data-quiz-form]');
      return;
    }
    console.log('Quiz: Form element found');
    
    const container = document.querySelector('[data-quiz-question-container]');
    if (!container) {
      console.error('Quiz: Container not found during initQuiz!');
      // Retry initialization
      setTimeout(() => initQuiz(), 200);
      return;
    }
    
    // Ensure currentStep and answers are reset (in case restoreQuizProgress modified it)
    // Only restore if user explicitly requested to resume via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const resume = urlParams.get('resume');
    const savedProgress = sessionStorage.getItem('veluna_quiz_progress');
    
    // CRITICAL: Always verify answers are empty unless we're explicitly resuming
    // This prevents pre-selection bugs - answers should only be set by restoreQuizProgress()
    if (!isResuming) {
      // Force clear answers again (safety check in case something set them)
      answers = Object.create(null);
      currentStep = 0;
      console.log('Quiz: initQuiz() - Verified answers are empty', Object.keys(answers).length === 0 ? 'SUCCESS' : 'ERROR: answers not empty!', 'Keys:', Object.keys(answers));
    } else {
      console.log('Quiz: initQuiz() - Resuming, answers:', Object.keys(answers || {}));
    }
    
    // Track start time for analytics
    sessionStorage.setItem('veluna_quiz_start_time', Date.now().toString());
    
    // Track quiz start with UTM parameters
    const utmParams = window.VelunaUtmPersistence ? window.VelunaUtmPersistence.getUtmForAnalytics() : {};
    
    if (typeof gtag !== 'undefined') {
      gtag('event', 'quiz_start', {
        'event_category': 'Quiz',
        'value': 1,
        'utm_source': utmParams.utm_source || '',
        'utm_medium': utmParams.utm_medium || '',
        'utm_campaign': utmParams.utm_campaign || '',
        'utm_content': utmParams.utm_content || ''
      });
    }
    
    // Store UTMs in sessionStorage for quiz completion
    if (Object.keys(utmParams).length > 0) {
      sessionStorage.setItem('veluna_quiz_utm_params', JSON.stringify(utmParams));
    }
    
    // Console log for verification
    console.log('[Veluna Quiz] quiz_start event fired with UTMs:', utmParams);
    
    // Set total steps
    const totalStepsEl = document.querySelector('[data-quiz-total-steps]');
    if (totalStepsEl) {
      totalStepsEl.textContent = QUIZ_QUESTIONS.length;
    }
    
    // Ensure container is visible before rendering
    container.style.display = '';
    container.style.visibility = 'visible';
    container.style.opacity = '1';
    
    // Check if fallback question exists and integrate it
    const fallback = document.querySelector('[data-quiz-fallback-question]');
    if (fallback && currentStep === 0) {
      // Remove fallback and render properly
      fallback.remove();
    }
    
    // Render current step (0 if fresh start, or saved step if resuming)
    renderQuestion(currentStep);
    updateProgress();
    
    // Verify question was rendered after a short delay
    setTimeout(() => {
      const questionContent = container.querySelector('h2');
      if (!questionContent) {
        console.warn('Quiz: Question not rendered, retrying...');
        renderQuestion(currentStep);
      }
    }, 500);
    
    // Event listeners (Previous button only - submit is auto-triggered)
    const prevBtn = document.querySelector('[data-quiz-prev]');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', handlePrev);
    }
  }
  
  /**
   * Render question
   */
  function renderQuestion(step) {
    logDiag('RENDER', `renderQuestion called for step ${step}`);
    captureState(`before-render-step-${step}`);
    
    // MOBILE FIX: Delay render to let browser finish restoration attempts first
    if (isMobileDevice && !renderQuestion._delayApplied) {
      logDiag('RENDER', 'Applying mobile delay');
      renderQuestion._delayApplied = true;
      setTimeout(() => {
        renderQuestion._delayApplied = false;
        renderQuestion(step);
      }, 100);
      return;
    }
    
    const question = QUIZ_QUESTIONS[step];
    const container = document.querySelector('[data-quiz-question-container]');
    
    // DEBUG: Log current state for Q3/Q4
    if (step >= 2) {
      console.log(`[Q${step+1} DEBUG] Rendering step ${step}, question: ${question ? question.id : 'null'}, saved answers:`, JSON.stringify(answers, null, 2));
    }
    
    // IMMEDIATE: Clear ALL radios and labels in entire form before rendering
    const allRadios = document.querySelectorAll('[data-quiz-form] input[type="radio"]');
    const allLabels = document.querySelectorAll('[data-quiz-form] .veluna-quiz__option');
    allRadios.forEach(r => {
      r.checked = false;
      r.removeAttribute('checked');
      r.removeAttribute('data-selected');
    });
    allLabels.forEach(l => {
      l.removeAttribute('data-selected');
      l.classList.remove('selected');
      l.classList.remove('veluna-quiz__option--selected');
    });
    
    // FORCE: Remove all selected classes from previous render
    const allOptions = document.querySelectorAll('.veluna-quiz__option.selected');
    allOptions.forEach(opt => opt.classList.remove('selected'));
    
    // Clear any pending advance timeout when rendering (prevents conflicts when going back)
    if (advanceTimeout) {
      clearTimeout(advanceTimeout);
      advanceTimeout = null;
    }
    
    // Reset advance state when rendering (ensures responsive UI, especially after going back)
    isAdvancing = false;
    
    if (!container) {
      console.error('Quiz: Question container not found! Looking for [data-quiz-question-container]');
      // Retry finding container
      setTimeout(() => {
        const retryContainer = document.querySelector('[data-quiz-question-container]');
        if (retryContainer && question) {
          renderQuestion(step);
        }
      }, 100);
      return;
    }
    if (!question) {
      console.error('Quiz: Question not found for step', step);
      return;
    }
    console.log('Quiz: Rendering question', step, question.id);

    // Drop any saved answers for this and later steps to avoid visual preselection when navigating back/forward
    for (let i = step; i < QUIZ_QUESTIONS.length; i++) {
      const q = QUIZ_QUESTIONS[i];
      if (q && answers && answers[q.id]) {
        delete answers[q.id];
      }
    }

    // Generate a unique radio name per render to break browser form state restoration
    const radioName = `question_${step}_${Date.now()}`;
    
    const isLastQuestion = step === QUIZ_QUESTIONS.length - 1;
    let dynamicName = '';
    
    // CRITICAL: Clear any data-selected from the previous question
    const allPreviousOptions = document.querySelectorAll('[data-quiz-form] .veluna-quiz__option');
    allPreviousOptions.forEach(opt => opt.removeAttribute('data-selected'));
    
    // Ensure container is visible
    container.style.display = '';
    container.style.visibility = 'visible';
    container.style.opacity = '1';
    
    // Add slide animation class
    container.classList.add('veluna-quiz__question--entering');
    setTimeout(() => {
      container.classList.remove('veluna-quiz__question--entering');
    }, 300);
    
    try {
      // Get microcopy for this step
      const subheadline = QUIZ_MICROCOPY.subheadlines[question.id] || '';
      
      // Create Previous button HTML to place above options (only show if not first question)
      const prevButtonHtml = step > 0 ? `
        <div class="veluna-quiz__prev-button-wrapper" style="margin-bottom: 1.5rem;">
          <button type="button" class="veluna-btn veluna-btn-secondary" data-quiz-prev-question style="display: inline-block;">
            ← Previous
          </button>
        </div>
      ` : '';
      
      container.innerHTML = `
        <p class="veluna-quiz__helper" style="font-size: 0.875rem; color: var(--text-muted, #666); margin-bottom: 0.5rem; line-height: 1.3; text-align: center;">${escapeHtml(QUIZ_MICROCOPY.preQuestionHelper)}</p>
        <h2 class="veluna-heading-md">${escapeHtml(question.question)}</h2>
        ${subheadline ? `<p class="veluna-quiz__subheadline" style="font-size: 0.9375rem; color: var(--text-secondary, #555); margin-top: 0.5rem; margin-bottom: 1.5rem; line-height: 1.5; text-align: center;">${escapeHtml(subheadline)}</p>` : ''}
        ${prevButtonHtml}
        <div class="veluna-quiz__options">
          ${question.options.map((opt, idx) => {
            const isFeatured = opt.featured === true;
            const optionClass = isFeatured ? 'veluna-quiz__option veluna-quiz__option--featured' : 'veluna-quiz__option';
            return `
            <label class="${optionClass}">
              <input type="radio" 
                     name="question_${step}" 
                     value="${opt.value}" 
                     data-category="${opt.category || ''}"
                     data-question-id="${question.id}"
                     autocomplete="off">
              ${opt.icon ? `<span class="veluna-quiz__option-icon">${opt.icon}</span>` : ''}
              <span class="veluna-quiz__option-text">${escapeHtml(opt.label)}</span>
              ${isFeatured ? '<span class="veluna-quiz__option-badge">Recommended</span>' : ''}
              <span class="veluna-quiz__option-checkmark">✓</span>
            </label>
          `;
          }).join('')}
        </div>
      `;

      logDiag('RENDER', 'innerHTML injected', { step });
      captureState(`after-innerHTML-step-${step}`);

      // IMMEDIATE: Clear any browser restoration that happened during innerHTML
      const immediateRadios = container.querySelectorAll('input[type="radio"]');
      immediateRadios.forEach(r => {
        r.checked = false;
        r.removeAttribute('checked');
        r.disabled = false; // Ensure they're NOT disabled yet (we'll disable next)
      });

      // SAFETY: Force-uncheck any radios post-render and disable autofill
      try {
        const formEl = document.querySelector('[data-quiz-form]');
        if (formEl) {
          formEl.setAttribute('autocomplete', 'off');
          formEl.setAttribute('data-lpignore', 'true');
          formEl.setAttribute('autocorrect', 'off');
          formEl.setAttribute('autocapitalize', 'off');
          formEl.setAttribute('spellcheck', 'false');
          // No form.reset() since we changed from <form> to <div> to prevent browser restoration
        }
        
        // Get NEW radios after innerHTML was set
        const radios = container.querySelectorAll('input[type="radio"]');

        // Assign a unique name per render to break browser form-state restoration
        dynamicName = `question_${step}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        currentRadioName = dynamicName; // CRITICAL: Store globally so Previous button can find radios
        radios.forEach(r => {
          r.setAttribute('name', dynamicName);
        });
        const labels = container.querySelectorAll('.veluna-quiz__option');
        
        let checkedCount = 0;
        radios.forEach(r => {
          if (r.checked) checkedCount++;
          r.checked = false;
          r.removeAttribute('checked');
          r.setAttribute('autocomplete', 'off');
          // CRITICAL ON MOBILE: Disable immediately to prevent ANY browser restoration
          if (isMobileDevice) {
            r.disabled = true;
          }
        });
        
        // CRITICAL: Remove .selected class from all labels for this step
        labels.forEach(label => {
          label.classList.remove('selected');
          label.classList.remove('veluna-quiz__option--selected');
          // Override CSS with inline style to ensure no visual selection
          label.style.borderColor = '';
          label.style.background = '';
        });
        
        // DEBUG for Q3/Q4
        if (step >= 2) {
          console.log(`[Q${step+1} DEBUG] Post-render: ${radios.length} radios, ${checkedCount} were checked, cleared ${labels.length} labels`);
        }
        
        logDiag('RENDER', 'Radios cleared immediately', { step, checkedCount });
        captureState(`after-immediate-clear-step-${step}`);
        
        // Run again on next tick
        setTimeout(() => {
          logDiag('RENDER', 'Next tick cleanup starting', { step });
          captureState(`before-clone-step-${step}`);
          
          // CRITICAL ON MOBILE: Replace all radios with clones to break browser restoration
          if (isMobileDevice) {
            const radiosToReplace = container.querySelectorAll('input[type="radio"]');
            const labelsToForce = container.querySelectorAll('.veluna-quiz__option');
            
            radiosToReplace.forEach(r => {
              const clone = r.cloneNode(true);
              clone.checked = false;
              clone.removeAttribute('checked');
              clone.disabled = true; // Keep disabled on mobile
              r.parentNode.replaceChild(clone, r);
            });
            
            // NUCLEAR: Force inline styles to override ANY CSS
            labelsToForce.forEach(label => {
              label.removeAttribute('data-selected');
              label.classList.remove('selected');
              label.style.cssText = 'border-color: #eee !important; background: transparent !important;';
            });
            
            logDiag('RENDER', 'Radios cloned and labels force-styled on mobile', { step, radios: radiosToReplace.length, labels: labelsToForce.length });
            captureState(`after-clone-step-${step}`);
          }
          
          const radiosLate = container.querySelectorAll('input[type="radio"]');
          const labelsLate = container.querySelectorAll('.veluna-quiz__option');
          let checkedLate = 0;
          
          radiosLate.forEach(r => {
            if (r.checked) checkedLate++;
            r.checked = false;
            r.removeAttribute('checked');
            r.removeAttribute('data-selected');
            
            // MOBILE FIX: On mobile, be even more aggressive about clearing state
            if (isMobileDevice) {
              r.removeAttribute('data-checked');
              r.removeAttribute('aria-checked');
            }
          });
          
          labelsLate.forEach(label => {
            label.classList.remove('selected');
            label.style.borderColor = '';
            label.style.background = '';
          });
          
          if (step >= 2 && (checkedLate > 0 || document.querySelector('.veluna-quiz__option.selected'))) {
            console.log(`[Q${step+1} DEBUG] Post-render tick 2: ${checkedLate} radios re-checked, ${document.querySelectorAll('[data-quiz-question-container] .veluna-quiz__option.selected').length} labels have selected class`);
          }
          
          // WATCH for mutations on this step's radios (for debugging)
          if (step >= 2) {
            const observer = new MutationObserver(mutations => {
              mutations.forEach(m => {
                if (m.type === 'attributes' && m.attributeName === 'checked') {
                  console.log(`[Q${step+1} DEBUG] MUTATION: radio.checked changed!`, m.target.value);
                }
                if (m.type === 'attributes' && m.target.classList.contains('veluna-quiz__option')) {
                  if (m.attributeName === 'class' && m.target.classList.contains('selected')) {
                    console.log(`[Q${step+1} DEBUG] MUTATION: .selected class added!`);
                  }
                }
              });
            });
            
            radiosLate.forEach(r => {
              observer.observe(r, { attributes: true, attributeFilter: ['checked'] });
            });
            labelsLate.forEach(label => {
              observer.observe(label, { attributes: true, attributeFilter: ['class'] });
            });
            
            setTimeout(() => observer.disconnect(), 2000);
          }
        }, 0);
      } catch (e) {
        console.warn('Quiz: Post-render radio reset failed', e);
      }
      
      // Attach event listeners for smart advance
      // Pass the dynamic name to listeners so we bind to the correct radios
      attachOptionListeners(step, isLastQuestion, dynamicName);

      // Mobile: ensure current question fits in viewport without hiding any elements
      setTimeout(applyMobileNoScrollFit, 0);
      
      // Attach Previous button event listener (button is now in question container, above options)
      const prevBtnInQuestion = container.querySelector('[data-quiz-prev-question]');
      if (prevBtnInQuestion) {
        prevBtnInQuestion.addEventListener('click', handlePrev);
      }
      
      // Hide the original navigation button (keep it hidden in navigation div)
      const originalNavBtn = document.querySelector('.veluna-quiz__navigation [data-quiz-prev]');
      if (originalNavBtn) {
        originalNavBtn.style.display = 'none';
      }
      
      // Verify content was rendered
      const renderedContent = container.querySelector('h2');
      if (!renderedContent) {
        console.error('Quiz: Content was not rendered properly, retrying...');
        setTimeout(() => renderQuestion(step), 100);
      } else {
        console.log('Quiz: Question rendered successfully', step);
        // Remove fallback question if it exists
        const fallback = document.querySelector('[data-quiz-fallback-question]');
        if (fallback) {
          fallback.remove();
        }

        // One more fit pass after fallback removal
        setTimeout(applyMobileNoScrollFit, 0);
      }
    } catch (error) {
      console.error('Quiz: Error rendering question:', error);
      // Retry on error
      setTimeout(() => renderQuestion(step), 200);
    }
  }
  
  /**
   * Attach event listeners for smart advance
   */
  function attachOptionListeners(step, isLastQuestion, radioName) {
    const effectiveName = radioName || `question_${step}`;
    const options = document.querySelectorAll(`input[name="${effectiveName}"]`);
    
    // CRITICAL: Clear data-selected from ALL options (in case it's set from previous question)
    const allOptions = document.querySelectorAll('[data-quiz-form] .veluna-quiz__option');
    allOptions.forEach(opt => opt.removeAttribute('data-selected'));
    
    // CRITICAL: Force uncheck all radios for this step before attaching listeners
    // This prevents browser form state restoration from interfering
    try {
      // No form.reset() since we changed from <form> to <div> to prevent browser restoration
      options.forEach(opt => {
        opt.checked = false;
        opt.removeAttribute('checked');
        // On mobile, radios were disabled in renderQuestion to prevent browser restoration
        // Don't re-enable them here; they'll be enabled only during click window
      });
    } catch (e) {
      console.warn('Quiz: Pre-listener reset failed', e);
    }
    
    if (options.length === 0) {
      console.warn(`Quiz: No options found for step ${step}, retrying...`);
      // Retry after a short delay (helps with mobile rendering)
      setTimeout(() => {
        const retryOptions = document.querySelectorAll(`input[name="${effectiveName}"]`);
        if (retryOptions.length > 0) {
          attachOptionListeners(step, isLastQuestion, effectiveName);
        }
      }, 100);
      return;
    }
    
    // Shared function to handle option selection (works for both change events and clicking already-selected options)
    function handleOptionSelection(inputElement, event) {
      logDiag('USER-INTERACTION', 'handleOptionSelection called', {
        value: inputElement.value,
        isTrusted: event ? event.isTrusted : null,
        eventType: event ? event.type : null
      });
      
      // Stop propagation to prevent event bubbling issues (especially on mobile)
      if (event) {
        event.stopPropagation();
      }

      // Ignore synthetic/non-user events to block browser autofill/restoration from selecting
      if (event && event.isTrusted === false) {
        console.warn('Quiz: Ignoring synthetic selection event');
        logDiag('USER-INTERACTION', 'Synthetic event blocked', { value: inputElement.value });
        return;
      }
      
      // Double-check isAdvancing (handles rapid clicks on mobile)
      if (isAdvancing) {
        console.log('Quiz: Ignoring selection - already advancing');
        return;
      }
      
      const questionId = inputElement.dataset.questionId;
      const value = inputElement.value;
      const category = inputElement.dataset.category;
      
      logDiag('USER-INTERACTION', 'Processing valid selection', { questionId, value, category });
      
      // CRITICAL: Do NOT set radio.checked - use data-selected attribute instead to avoid form restoration
      // Remove data-selected from all options for this step
      const allOptions = document.querySelectorAll('[data-quiz-question-container] .veluna-quiz__option');
      allOptions.forEach(opt => opt.removeAttribute('data-selected'));
      
      // Mark only this option as selected via data attribute
      const optionLabel = inputElement.closest('.veluna-quiz__option');
      if (optionLabel) {
        optionLabel.setAttribute('data-selected', 'true');
        logDiag('USER-INTERACTION', 'data-selected set', { value });
        captureState(`after-user-selection-${value}`);
      }
      
      // CRITICAL: Immediately uncheck the radio so browser can't restore it on next load
      inputElement.checked = false;
      inputElement.removeAttribute('checked');
      // On mobile, also re-disable to prevent any further browser attempts
      if (isMobileDevice) {
        inputElement.disabled = true;
      }
      
      // (Micro-feedback removed — clean auto-advance, interstitials handle engagement)
      
      // Save answer immediately
      // For Q3 (sleep_schedule), if category is null, we'll use Q1 category in scoring
      answers[questionId] = {
        question: questionId,
        value: value,
        category: category || null // Store null explicitly for "keep Q1" options
      };
      
      // Save progress for abandonment recovery
      saveProgress();
      
      // Clear any existing advance timeout (in case user clicks quickly)
      if (advanceTimeout) {
        clearTimeout(advanceTimeout);
        advanceTimeout = null;
      }
      
      // Smart advance: Visual feedback delay, then auto-advance or auto-submit
      isAdvancing = true;
      
      advanceTimeout = setTimeout(() => {
        advanceTimeout = null;
        if (isLastQuestion) {
          // Final question: Auto-submit immediately (no button click needed)
          isAdvancing = false;
          handleSubmit(null); // Pass null since we're auto-submitting
        } else {
          // Check for interstitial slides before advancing
          const interstitialGroup = INTERSTITIAL_SLIDES.find(g => g.afterStep === currentStep);
          if (interstitialGroup && interstitialGroup.slides.length > 0) {
            // Queue interstitial slides, then advance to next question
            pendingInterstitials = interstitialGroup.slides.slice();
            isAdvancing = false;
            showNextInterstitial();
          } else {
            // Normal advance
            currentStep++;
            renderQuestion(currentStep);
            updateProgress();
            isAdvancing = false;
          }
        }
      }, ADVANCE_DELAY);
    }
    
    // MOBILE FIX: Prevent radios from ever being checked - use only data-selected for state
    options.forEach((radio) => {
      // Prevent default to stop browser from checking the radio
      radio.addEventListener('click', function(e) {
        e.preventDefault(); // Critical: prevent radio from being checked by browser
        // On mobile, enable the radio momentarily for the browser to process the click
        if (isMobileDevice) {
          this.disabled = false;
        }
        if (!isAdvancing) {
          handleOptionSelection(this, e);
        }
      }, true); // Use capture to catch event early
      
      // Block change events completely - we handle selection manually
      radio.addEventListener('change', function(e) {
        e.preventDefault();
        e.stopPropagation();
        // Force uncheck immediately
        this.checked = false;
        // Re-disable on mobile
        if (isMobileDevice) {
          this.disabled = true;
        }
      }, true);
    });

    // Additional safety: bind click handlers on labels directly
    const labels = document.querySelectorAll('[data-quiz-question-container] .veluna-quiz__option');
    labels.forEach(label => {
      label.addEventListener('click', function(e) {
        const input = this.querySelector('input[type="radio"]');
        if (input) {
          // On mobile, enable radio temporarily so click can register
          if (isMobileDevice) {
            input.disabled = false;
          }
          if (!isAdvancing) {
            handleOptionSelection(input, e);
          }
        }
      });
    });
  }
  
  // ─── INTERSTITIAL RENDERING ────────────────────────────────────────────────

  function showNextInterstitial() {
    if (pendingInterstitials.length === 0) {
      // All interstitials done — advance to next question
      interstitialActive = false;
      currentStep++;
      renderQuestion(currentStep);
      updateProgress();
      return;
    }

    interstitialActive = true;
    const slide = pendingInterstitials.shift();
    const container = document.querySelector('[data-quiz-question-container]');
    if (!container) return;

    // Build slide HTML
    let innerHtml = '';
    var imgUrl = slide.imageFallback || slide.image || '';
    var imgHtml = imgUrl ? `<div class="veluna-interstitial__image"><img src="${imgUrl}" alt="" loading="eager"></div>` : '';

    if (slide.benefits && slide.benefits.length) {
      // Slide with image + headline + staggered benefit lines (aspirational-1)
      innerHtml = `
        <div class="veluna-interstitial veluna-interstitial--ripple" aria-live="polite">
          ${imgHtml}
          <h2 class="veluna-interstitial__headline">${escapeHtml(slide.headline)}</h2>
          <ul class="veluna-interstitial__benefits">
            ${slide.benefits.map((b, i) => `<li class="veluna-interstitial__benefit" style="animation-delay: ${0.35 + i * 0.18}s">${escapeHtml(b)}</li>`).join('')}
          </ul>
          <button type="button" class="veluna-interstitial__cta" style="animation-delay: ${0.35 + slide.benefits.length * 0.18 + 0.15}s">${escapeHtml(slide.cta)}</button>
        </div>
      `;
    } else {
      // Slide with image + headline + subtext (aspirational-2)
      innerHtml = `
        <div class="veluna-interstitial veluna-interstitial--breathe" aria-live="polite">
          ${imgHtml}
          <h2 class="veluna-interstitial__headline">${escapeHtml(slide.headline)}</h2>
          <p class="veluna-interstitial__subtext">${escapeHtml(slide.subtext)}</p>
          <button type="button" class="veluna-interstitial__cta" style="animation-delay: 0.8s">${escapeHtml(slide.cta)}</button>
        </div>
      `;
    }

    // Fade out current content then replace
    container.style.opacity = '0';
    container.style.transform = 'translateY(8px)';
    container.style.transition = 'opacity 0.2s ease, transform 0.2s ease';

    setTimeout(() => {
      container.innerHTML = innerHtml;
      container.style.opacity = '1';
      container.style.transform = 'translateY(0)';

      // Bind CTA
      const cta = container.querySelector('.veluna-interstitial__cta');
      if (cta) {
        cta.addEventListener('click', function() {
          showNextInterstitial();
        });
      }

      // Mobile fit
      setTimeout(applyMobileNoScrollFit, 50);
    }, 220);
  }

  // ─── GOAL PROJECTION + MICRO-GATE ──────────────────────────────────────────

  function showGoalProjection() {
    const container = document.querySelector('[data-quiz-question-container]');
    if (!container) {
      // Fallback: skip gate and go straight to redirect
      calculateRecommendation();
      return;
    }

    container.innerHTML = `
      <div class="veluna-goal-projection" aria-live="polite">
        <div class="veluna-goal-projection__glow"></div>
        <span class="veluna-goal-projection__badge">Analysis complete</span>
        <h2 class="veluna-goal-projection__headline">Based on your answers, we expect meaningful improvement in your sleep quality</h2>
        <p class="veluna-goal-projection__sub">Your personalized ritual has been crafted to target the exact areas you identified.</p>
        <button type="button" class="veluna-goal-projection__cta">
          I\u2019m ready! Show me my ritual
        </button>
      </div>
    `;

    const cta = container.querySelector('.veluna-goal-projection__cta');
    if (cta) {
      cta.addEventListener('click', function() {
        // Add exit animation
        container.style.opacity = '0';
        container.style.transform = 'scale(0.97)';
        container.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        setTimeout(() => {
          calculateRecommendation();
        }, 300);
      });
    }

    // Mobile fit
    setTimeout(applyMobileNoScrollFit, 50);
  }

  /**
   * Update progress bar
   */
  function updateProgress() {
    // Ensure currentStep is within valid bounds
    if (currentStep < 0) currentStep = 0;
    if (currentStep >= QUIZ_QUESTIONS.length) currentStep = QUIZ_QUESTIONS.length - 1;
    
    const progress = ((currentStep + 1) / QUIZ_QUESTIONS.length) * 100;
    const progressFill = document.querySelector('[data-quiz-progress]');
    const currentStepEl = document.querySelector('[data-quiz-current-step]');
    
    if (progressFill) {
      progressFill.style.width = `${Math.min(100, Math.max(0, progress))}%`;
    }
    
    if (currentStepEl) {
      // Ensure step number is valid (1-4)
      const stepNumber = Math.min(QUIZ_QUESTIONS.length, Math.max(1, currentStep + 1));
      currentStepEl.textContent = stepNumber;
    }
  }
  
  /**
   * Handle previous button
   */
  function handlePrev(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Clear any pending advance timeout to prevent interference
    if (advanceTimeout) {
      clearTimeout(advanceTimeout);
      advanceTimeout = null;
    }
    
    // Reset advance state immediately
    isAdvancing = false;
    
    // Ensure we don't go below step 0
    if (currentStep <= 0) {
      currentStep = 0;
      return;
    }
    
    currentStep--;
    // Clear saved answer for this step so nothing appears selected on return
    try {
      const prevQuestion = QUIZ_QUESTIONS[currentStep];
      if (prevQuestion && answers && answers[prevQuestion.id]) {
        delete answers[prevQuestion.id];
      }
    } catch (err) {
      console.warn('Quiz: Could not clear previous step answer', err);
    }
    
    // Render the previous question
    renderQuestion(currentStep);
    updateProgress();
    
    // CRITICAL: Use the dynamically-set radio name after render
    // After renderQuestion(), the currentRadioName will be updated to the new dynamic name
    setTimeout(() => {
      const queryName = currentRadioName || `question_${currentStep}`;
      const options = document.querySelectorAll(`input[name="${queryName}"]`);
      if (options.length === 0) {
        // If options still aren't found, retry rendering with old static name as fallback
        console.warn('Quiz: Options not found with dynamic name', queryName, 're-rendering...');
        renderQuestion(currentStep);
      }
    }, 50);
  }
  
  /**
   * Handle submit (final question) - now auto-triggered
   */
  function handleSubmit(e) {
    // Allow null event for auto-submit
    if (e) {
      e.preventDefault();
    }
    
    // Check for selected via data attribute (not checked property to avoid form restoration issues)
    const selectedLabel = document.querySelector('[data-quiz-question-container] .veluna-quiz__option[data-selected="true"]');
    const selected = selectedLabel ? selectedLabel.querySelector('input[type="radio"]') : null;
    
    if (!selected) {
      // If no selection, check if answer was already saved via smart advance
      const question = QUIZ_QUESTIONS[currentStep];
      if (!answers[question.id]) {
        // Should not happen since we save on change, but safety check
        return;
      }
    } else {
      // Save final answer if not already saved
      const question = QUIZ_QUESTIONS[currentStep];
      if (!answers[question.id]) {
        answers[question.id] = {
          question: question.id,
          value: selected.value,
          category: selected.dataset.category
        };
      }
    }
    
    // Show calculating state in UI
    const questionContainer = document.querySelector('[data-quiz-question-container]');
    if (questionContainer) {
      questionContainer.innerHTML = `
        <div class="veluna-quiz__calculating">
          <h2 class="veluna-heading-md" style="margin-bottom: 0.25rem;">Analyzing your answers</h2>
          <p style="font-size: 0.9375rem; color: var(--text-muted, #666); margin: 0 0 1.5rem 0;">Matching you to the right ritual</p>
          <div class="veluna-quiz__calc-bar">
            <div class="veluna-quiz__calc-fill" id="calc-fill"></div>
          </div>
          <div class="veluna-quiz__calc-percent" id="calc-percent">0 %</div>
          <div style="display:flex;flex-direction:column;gap:14px;width:100%;max-width:400px;margin-top:0.75rem;">
            <div class="veluna-quiz__calc-step" id="calc-step-0">
              <span class="veluna-quiz__calc-step-icon">1</span>
              <span class="veluna-quiz__calc-step-label">Reviewing sleep profile</span>
            </div>
            <div class="veluna-quiz__calc-step" id="calc-step-1">
              <span class="veluna-quiz__calc-step-icon">2</span>
              <span class="veluna-quiz__calc-step-label">Matching ritual components</span>
            </div>
            <div class="veluna-quiz__calc-step" id="calc-step-2">
              <span class="veluna-quiz__calc-step-icon">3</span>
              <span class="veluna-quiz__calc-step-label">Personalizing your recommendation</span>
            </div>
          </div>
        </div>
      `;

      // Animate the multi-step processing
      var calcFill = document.getElementById('calc-fill');
      var calcPercent = document.getElementById('calc-percent');
      var steps = [
        document.getElementById('calc-step-0'),
        document.getElementById('calc-step-1'),
        document.getElementById('calc-step-2')
      ];

      var stepConfig = [
        { start: 0, end: 35, duration: 1800 },
        { start: 35, end: 70, duration: 1600 },
        { start: 70, end: 100, duration: 1400 }
      ];

      var currentPct = 0;

      function animateStep(idx) {
        if (idx >= stepConfig.length) {
          setTimeout(function() { showGoalProjection(); }, 400);
          return;
        }

        var cfg = stepConfig[idx];

        if (idx > 0 && steps[idx - 1]) {
          steps[idx - 1].classList.remove('active');
          steps[idx - 1].classList.add('done');
          steps[idx - 1].querySelector('.veluna-quiz__calc-step-icon').textContent = '\u2713';
        }

        if (steps[idx]) steps[idx].classList.add('active');

        var startPct = cfg.start;
        var endPct = cfg.end;
        var startTime = performance.now();

        function tick(now) {
          var elapsed = now - startTime;
          var progress = Math.min(elapsed / cfg.duration, 1);
          var eased = 1 - Math.pow(1 - progress, 3);
          currentPct = Math.round(startPct + (endPct - startPct) * eased);

          if (calcFill) calcFill.style.width = currentPct + '%';
          if (calcPercent) calcPercent.textContent = currentPct + ' %';

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            setTimeout(function() { animateStep(idx + 1); }, 300);
          }
        }

        requestAnimationFrame(tick);
      }

      animateStep(0);
    } else {
      setTimeout(function() { showGoalProjection(); }, 500);
    }
    
    // Hide navigation buttons
    var prevBtn = document.querySelector('[data-quiz-prev]');
    if (prevBtn) prevBtn.style.display = 'none';
  }
  
  /**
   * Calculate recommendation based on weighted scoring (not simple vote counting)
   * Optimized based on deep product analysis - maps directly to value propositions
   */
  function calculateRecommendation() {
    // Weighted category scoring
    const categoryScores = {};
    
    // Track Q1 category (primary pain point - highest weight)
    let primaryCategory = null;
    
    // First pass: Collect Q1 category and handle Q3 override
    Object.values(answers).forEach(answer => {
      if (!answer.question) return;
      
      // Track Q1 category (primary pain point)
      if (answer.question === 'sleep_challenge' && answer.category) {
        primaryCategory = answer.category;
      }
      
      // Q3 "irregular schedule" overrides to circadian-reset
      if (answer.question === 'sleep_schedule' && answer.value === 'irregular') {
        const weight = QUESTION_WEIGHTS[answer.question] || 1.0;
        categoryScores['circadian-reset'] = (categoryScores['circadian-reset'] || 0) + weight;
      }
    });
    
    // Second pass: Add weighted scores (skip bundle types and Q3 null categories)
    Object.values(answers).forEach(answer => {
      if (!answer.question) return;
      
      const weight = QUESTION_WEIGHTS[answer.question] || 1.0;
      
      // Skip bundle-type categories (they're handled separately)
      if (answer.category === 'starter-set' || answer.category === 'booster-set' || answer.category === 'full-set') {
        return;
      }
      
      // Skip Q3 null categories (they don't add score, Q1 category is used instead)
      if (answer.question === 'sleep_schedule' && !answer.category) {
        // If Q3 is "regular" or "doesn't matter", reinforce Q1 category
        if (primaryCategory) {
          categoryScores[primaryCategory] = (categoryScores[primaryCategory] || 0) + (weight * 0.5); // Half weight for reinforcement
        }
        return;
      }
      
      // Skip if already handled (Q3 irregular schedule)
      if (answer.question === 'sleep_schedule' && answer.value === 'irregular') {
        return;
      }
      
      // Add weighted score for all other answers
      if (answer.category) {
        categoryScores[answer.category] = (categoryScores[answer.category] || 0) + weight;
      }
    });
    
    // Get top category (highest weighted score)
    let topCategory = 'mind-calm'; // Default fallback
    let maxScore = 0;
    
    Object.keys(categoryScores).forEach(cat => {
      // Skip bundle-type categories (starter-set, booster-set, full-set)
      if (cat === 'starter-set' || cat === 'booster-set' || cat === 'full-set') {
        return;
      }
      
      if (categoryScores[cat] > maxScore) {
        maxScore = categoryScores[cat];
        topCategory = cat;
      }
    });
    
    // If no category scored (edge case), use Q1 primary category
    if (maxScore === 0 && primaryCategory) {
      topCategory = primaryCategory;
    }
    
    // Bundle selection is intentionally fixed (recommendations are full system by design)
    const bundleType = 'full-set';
    
    // Generate dynamic benefits based on answer combinations
    const benefits = generateDynamicBenefits(topCategory, bundleType, answers);
    
    // Store results with enhanced data structure
    const results = {
      category: topCategory,
      bundleType: bundleType,
      answers: answers,
      scores: categoryScores,
      benefits: benefits,
      timestamp: Date.now(),
      version: '2.0'
    };
    
    // Save to localStorage and sessionStorage
    localStorage.setItem('veluna_quiz_results', JSON.stringify(results));
    sessionStorage.setItem('veluna_quiz_results', JSON.stringify(results));
    
    // Save progress (for abandonment recovery)
    sessionStorage.setItem('veluna_quiz_progress', JSON.stringify({
      currentStep: currentStep,
      answers: answers,
      timestamp: Date.now()
    }));
    
    // Save to Shopify via Customer API (if customer is logged in)
    saveToShopify(results);
    
    // Get UTM parameters from sessionStorage (stored at quiz start)
    let utmParams = {};
    try {
      const storedUtms = sessionStorage.getItem('veluna_quiz_utm_params');
      if (storedUtms) {
        utmParams = JSON.parse(storedUtms);
      }
    } catch (e) {
      // Fallback to current UTMs if sessionStorage fails
      utmParams = window.VelunaUtmPersistence ? window.VelunaUtmPersistence.getUtmForAnalytics() : {};
    }
    
    // Track quiz completion with analytics and UTM parameters
    if (typeof gtag !== 'undefined') {
      gtag('event', 'quiz_complete', {
        'event_category': 'Quiz',
        'event_label': `${topCategory}-${bundleType}`,
        'value': 1,
        'utm_source': utmParams.utm_source || '',
        'utm_medium': utmParams.utm_medium || '',
        'utm_campaign': utmParams.utm_campaign || '',
        'utm_content': utmParams.utm_content || ''
      });
    }
    
    // Console log for verification
    console.log('[Veluna Quiz] quiz_complete event fired with UTMs:', utmParams);
    
    // Track time spent
    const startTime = sessionStorage.getItem('veluna_quiz_start_time');
    if (startTime) {
      const timeSpent = Date.now() - parseInt(startTime, 10);
      if (typeof gtag !== 'undefined') {
        gtag('event', 'quiz_time', {
          'event_category': 'Quiz',
          'value': Math.round(timeSpent / 1000) // seconds
        });
      }
    }
    
    // Clear quiz progress since quiz is complete
    sessionStorage.removeItem('veluna_quiz_progress');
    
    // Mark quiz as complete to prevent beforeunload popup
    quizIsComplete = true;
    
    // Set quiz completed flag using state module (if available)
    if (window.VelunaRitualState && typeof window.VelunaRitualState.setQuizCompleted === 'function') {
      window.VelunaRitualState.setQuizCompleted();
    } else {
      // Fallback: set flag directly
      localStorage.setItem('veluna_quiz_completed', 'true');
    }
    
    // Set flag to indicate user is coming from quiz (for collection page routing)
    sessionStorage.setItem('veluna_from_quiz', 'true');
    
    // Set flag for My Ritual page routing (TILE-05)
    sessionStorage.setItem('veluna_came_from_quiz', 'true');
    
    // TILE-LUNA-021: Dispatch quiz completion event for Luna
    const quizCompletedEvent = new CustomEvent('quiz-completed', {
      detail: {
        category: topCategory,
        bundleType: bundleType,
        quizResults: {
          category: topCategory,
          bundleType: bundleType,
          bundle: bundleType.toLowerCase().replace(/\s+/g, '-')
        }
      }
    });
    window.dispatchEvent(quizCompletedEvent);
    
    // TILE-LUNA-021: Set localStorage flag
    localStorage.setItem('luna_quiz_completed', 'true');
    
    // PHASE 3: Redirect to My Ritual page (canonical quiz results destination)
    console.log('Quiz: Redirecting to My Ritual page', { topCategory, bundleType });
    const resultsUrl = buildQuizResultsUrl(topCategory, bundleType);
    console.log('Quiz: My Ritual URL:', resultsUrl);
    console.log('Quiz: Full redirect URL:', window.location.origin + resultsUrl);
    
    // Use replace to prevent back button from going to quiz
    // Small delay to ensure quizIsComplete flag is set
    setTimeout(() => {
      try {
        window.location.replace(resultsUrl);
      } catch (e) {
        console.error('Quiz: Error redirecting to My Ritual page:', e);
        console.error('Quiz: Attempted URL:', resultsUrl);
        // Fallback: try full URL
        window.location.replace(window.location.origin + resultsUrl);
      }
    }, 100);
  }
  
  /**
   * Generate dynamic benefits based on answer combinations (not hardcoded)
   */
  function generateDynamicBenefits(category, bundleType, answers) {
    const benefits = [];
    
    // Map answers to specific benefit keywords
    const answerBenefitMap = {
      // Q1: Sleep Challenge
      'racing_mind': 'Quiets racing thoughts before bed',
      'physical_tension': 'Releases physical tension and restlessness',
      'irregular_schedule': 'Resets your body clock for consistent sleep',
      'visible_stress': 'Breaks the stress-sleep-skin cycle',
      
      // Q2: Next-day feel
      'mental_drained': 'Helps quiet a busy mind so you wake up clear and calm',
      'body_heavy': 'Supports deeper recovery so your body feels restored',
      'out_of_sync': 'Supports a steadier rhythm so your energy aligns with your day',
      'look_tired': 'Supports overnight restoration so you wake up looking refreshed',
      
      // Q3: Sleep Schedule
      'irregular': 'Master your schedule regardless of time zone',
      'beauty_focus': 'Optimizes overnight skin repair',
      
      // Q4: Goals
      'quiet_mind': 'Eliminates sleep-related anxiety',
      'physical_recovery': 'Accelerates muscle repair and recovery',
      'reset_clock': 'Provides conscious control over your biological rhythm',
      'radiant_skin': 'Wakes you up with visible glow'
    };
    
    // Primary benefit from Q1 (sleep challenge)
    if (answers.sleep_challenge) {
      const q1Benefit = answerBenefitMap[answers.sleep_challenge.value];
      if (q1Benefit) benefits.push(q1Benefit);
    }
    
    // Secondary benefit from Q4 (goals) - if different from Q1
    if (answers.goals && answers.goals.value !== answers.sleep_challenge?.value) {
      const q4Benefit = answerBenefitMap[answers.goals.value];
      if (q4Benefit && !benefits.includes(q4Benefit)) {
        benefits.push(q4Benefit);
      }
    }
    
    // Q2 benefit signal
    if (answers.current_routine) {
      const q2Benefit = answerBenefitMap[answers.current_routine.value];
      if (q2Benefit && !benefits.includes(q2Benefit)) benefits.push(q2Benefit);
    }
    
    // Category-specific benefits (if not already covered)
    const categoryBenefits = {
      'mind-calm': 'Manages daytime cortisol spikes',
      'deep-restore': 'Promotes deep sleep cycles for HGH release',
      'sacred-soothe': 'Manages cortisol to prevent collagen degradation',
      'circadian-reset': 'Provides conscious control over your sleep-wake cycle'
    };
    
    if (categoryBenefits[category] && !benefits.includes(categoryBenefits[category])) {
      benefits.push(categoryBenefits[category]);
    }
    
    // Return top 3-4 benefits
    return benefits.slice(0, 4);
  }
  
  /**
   * Save quiz results to Shopify Customer Metafields (via Admin API)
   */
  async function saveToShopify(results) {
    // Check if customer is logged in
    const customerId = getCustomerId();
    if (!customerId) {
      // If not logged in, results are in localStorage and will be saved on login/checkout
      return;
    }
    
    try {
      // Create metafield data
      const metafieldData = {
        namespace: 'custom',
        key: 'quiz_results',
        value: JSON.stringify(results),
        type: 'json'
      };
      
      // Save via Shopify Customer API
      // Note: This requires a server-side endpoint or Shopify App
      // Check endpoint availability
      if (typeof window.velunaEndpointAvailable === 'function') {
        const available = await window.velunaEndpointAvailable('/apps/api/customer/quiz-results');
        if (!available) {
          console.warn('[VELUNA_GATE] skip /apps/api/customer/quiz-results');
          return; // Endpoint not available, skip silently
        }
      }
      
      // For now, we'll prepare the data structure
      const response = await fetch('/apps/api/customer/quiz-results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: customerId,
          metafield: metafieldData,
          tags: [
            'quiz-completed',
            `quiz-${results.category}`,
            `quiz-${results.bundleType}`,
            `quiz-v${results.version}`
          ]
        })
      });
      
      if (response.ok) {
        console.log('Quiz results saved to Shopify');
      } else {
        console.warn('Failed to save quiz results to Shopify, using localStorage fallback');
      }
    } catch (error) {
      console.warn('Error saving quiz results to Shopify:', error);
      // Fallback: Results are already in localStorage
    }
  }
  
  /**
   * Get customer ID from Shopify
   */
  function getCustomerId() {
    // Check if customer is logged in via Shopify customer account
    const customerData = document.querySelector('[data-customer-id]');
    if (customerData) {
      return customerData.dataset.customerId;
    }
    
    // Try to get from Shopify customer object (if available)
    if (typeof Shopify !== 'undefined' && Shopify.customer) {
      return Shopify.customer.id;
    }
    
    return null;
  }
  
  /**
   * Restore quiz progress (for abandonment recovery)
   * DISABLED: Always return false to start fresh quiz (no preselected answers)
   */
  function restoreQuizProgress() {
    // Clear all saved progress to prevent preselected answers
    sessionStorage.removeItem('veluna_quiz_progress');
    sessionStorage.removeItem('veluna_quiz_answers');
    sessionStorage.removeItem('veluna_quiz_utm_params');
    
    // Always start fresh
    currentStep = 0;
    answers = Object.create(null);
    isResuming = false;
    
    return false;
  }
  
  /**
   * Show resume option if quiz was abandoned
   */
  function showResumeOption() {
    const container = document.querySelector('[data-quiz-question-container]');
    if (!container) return;
    
    const resumeHTML = `
      <div class="veluna-quiz__resume">
        <p class="veluna-body">You have an incomplete quiz. Would you like to continue?</p>
        <div class="veluna-quiz__resume-actions">
          <button type="button" class="veluna-btn veluna-btn-primary" data-quiz-resume>
            Continue Quiz
          </button>
          <button type="button" class="veluna-btn veluna-btn-secondary" data-quiz-restart>
            Start Over
          </button>
        </div>
      </div>
    `;
    
    container.insertAdjacentHTML('beforebegin', resumeHTML);
    
    // Attach event listeners
    const resumeBtn = document.querySelector('[data-quiz-resume]');
    const restartBtn = document.querySelector('[data-quiz-restart]');
    
    if (resumeBtn) {
      resumeBtn.addEventListener('click', () => {
        document.querySelector('.veluna-quiz__resume')?.remove();
        renderQuestion(currentStep);
        updateProgress();
      });
    }
    
    if (restartBtn) {
      restartBtn.addEventListener('click', () => {
        sessionStorage.removeItem('veluna_quiz_progress');
        answers = {};
        currentStep = 0;
        document.querySelector('.veluna-quiz__resume')?.remove();
        renderQuestion(0);
        updateProgress();
      });
    }
  }
  
  /**
   * Save progress on each answer (for abandonment recovery)
   */
  function saveProgress() {
    // DISABLED: No longer saving progress to prevent preselected answers
    // Each quiz session now starts fresh
    // VERIFICATION: Deploy test marker - 2025-01-19
  }
  
  /**
   * Escape HTML
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  // Initialize when DOM is ready - Enhanced with retry logic
  function initializeQuiz() {
    console.log('Quiz: Attempting initialization...');
    
    const form = document.querySelector('[data-quiz-form]');
    const container = document.querySelector('[data-quiz-question-container]');
    
    if (!form) {
      console.error('Quiz: Form not found, retrying in 100ms...');
        setTimeout(initializeQuiz, 100);
        return;
    }
    
    if (!container) {
      console.error('Quiz: Container not found, retrying in 100ms...');
      setTimeout(initializeQuiz, 100);
        return;
    }
    
    console.log('Quiz: All elements found, initializing...');
    
    // Disable form/browser autofill features aggressively
    try {
      if (form) {
        form.setAttribute('autocomplete', 'off');
        form.setAttribute('data-lpignore', 'true');
        form.setAttribute('autocorrect', 'off');
        form.setAttribute('autocapitalize', 'off');
        form.setAttribute('spellcheck', 'false');
        // CRITICAL: Reset form IMMEDIATELY to clear browser's form state restoration
        form.reset();
      }
    } catch (e) {
      console.warn('Quiz: Form reset during init failed', e);
    }

    // Extra guard for mobile: force a fresh form state after attribute updates
    try {
      const formEl = document.querySelector('[data-quiz-form]');
      if (formEl) {
        if (isMobileDevice) {
          formEl.setAttribute('autocomplete', 'off');
          formEl.setAttribute('autocorrect', 'off');
          formEl.setAttribute('autocapitalize', 'off');
          formEl.style.display = 'block'; // Force reflow to clear cached rendering
        }
        formEl.reset();
      }
    } catch (e) {
      // Ignore reset errors
    }

    // CRITICAL: Always start with empty answers first (prevents pre-selection bugs)
    answers = Object.create(null);
    currentStep = 0;
    isResuming = false;
    
    // Clear any corrupted sessionStorage on fresh page load
    const urlParams = new URLSearchParams(window.location.search);
    const resume = urlParams.get('resume');
    
    if (resume === 'true') {
      // User wants to resume, try to restore
      if (restoreQuizProgress()) {
        // restoreQuizProgress already set isResuming and answers if successful
        initQuiz();
      } else {
        // No valid progress to restore, ensure fresh start
        sessionStorage.removeItem('veluna_quiz_progress');
        answers = Object.create(null);
        currentStep = 0;
        isResuming = false;
        console.log('Quiz: Resume requested but no valid progress - starting fresh');
        initQuiz();
      }
    } else {
      // Fresh start - clear any old progress
      sessionStorage.removeItem('veluna_quiz_progress');
      answers = Object.create(null);
      currentStep = 0;
      isResuming = false;
      console.log('Quiz: Fresh start - answers cleared', Object.keys(answers).length === 0, 'Keys:', Object.keys(answers));
      initQuiz();
    }
  }
  
  // PHASE 8: Quiz Abandonment Recovery
  let quizIsComplete = false; // Track if quiz is complete to prevent beforeunload on redirect
  
  // Exit modal cooldown (prevent repeated prompting)
  function getExitModalCooldown() {
    try {
      const cooldown = sessionStorage.getItem('veluna_quiz_exit_modal_cooldown');
      if (!cooldown) return null;
      const timestamp = parseInt(cooldown, 10);
      const now = Date.now();
      const cooldownDuration = 30 * 60 * 1000; // 30 minutes
      if (now - timestamp < cooldownDuration) {
        return timestamp + cooldownDuration - now; // Return remaining cooldown time
      }
      return null; // Cooldown expired
    } catch (e) {
      return null;
    }
  }
  
  function setExitModalCooldown() {
    try {
      sessionStorage.setItem('veluna_quiz_exit_modal_cooldown', Date.now().toString());
    } catch (e) {
      // Silent fail
    }
  }
  
  function initQuizAbandonmentRecovery() {
    // ⚠️ OLD IMPLEMENTATION DISABLED - New premium modal embedded in liquid section
    // The old JS-based modal was creating the weak form below footer
    // New modal is in sections/veluna-ritual-quiz.liquid with TEST_MODE enabled
    console.log('Quiz: Old abandonment recovery disabled - using new liquid-based modal');
    return;
  }
  
  // [DISABLED] function showAbandonmentModal() {
  // [DISABLED] // Check if modal already exists
  // [DISABLED] let modal = document.getElementById('veluna-quiz-abandonment-modal');
  // [DISABLED] if (modal) return;
  // [DISABLED] 
  // [DISABLED] // Get centralized copy (fallback to defaults if module not loaded)
  // [DISABLED] const copy = window.VelunaRitualCopy && window.VelunaRitualCopy.bonus && window.VelunaRitualCopy.bonus.exitIntent
  // [DISABLED] ? window.VelunaRitualCopy.bonus.exitIntent
  // [DISABLED] : {
  // [DISABLED] headline: "Save your progress",
  // [DISABLED] body: "We'll save where you left off and email your personalized results when you're ready. Complete the quiz to get your saved Ritual Guide by email.",
  // [DISABLED] emailPlaceholder: "Enter your email",
  // [DISABLED] primaryCTA: "Save Progress",
  // [DISABLED] secondaryLink: "Continue Quiz"
  // [DISABLED] };
  // [DISABLED] 
  // [DISABLED] // Create modal with new copy and guest option
  // [DISABLED] modal = document.createElement('div');
  // [DISABLED] modal.id = 'veluna-quiz-abandonment-modal';
  // [DISABLED] modal.className = 'veluna-quiz-abandonment-modal';
  // [DISABLED] modal.setAttribute('role', 'dialog');
  // [DISABLED] modal.setAttribute('aria-labelledby', 'veluna-quiz-abandonment-modal-headline');
  // [DISABLED] modal.setAttribute('aria-modal', 'true');
  // [DISABLED] 
  // [DISABLED] // Escape HTML helper
  // [DISABLED] function escapeHtml(text) {
  // [DISABLED] const div = document.createElement('div');
  // [DISABLED] div.textContent = text;
  // [DISABLED] return div.innerHTML;
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] modal.innerHTML = `
  // [DISABLED] <div class="veluna-quiz-abandonment-modal__backdrop"></div>
  // [DISABLED] <div class="veluna-quiz-abandonment-modal__dialog">
  // [DISABLED] <button class="veluna-quiz-abandonment-modal__close" aria-label="Close">&times;</button>
  // [DISABLED] <h3 id="veluna-quiz-abandonment-modal-headline">${escapeHtml(copy.headline)}</h3>
  // [DISABLED] <p>${escapeHtml(copy.body)}</p>
  // [DISABLED] <form id="veluna-quiz-abandonment-form">
  // [DISABLED] <input type="email" placeholder="${escapeHtml(copy.emailPlaceholder)}" required aria-label="Email address">
  // [DISABLED] <button type="submit">${escapeHtml(copy.primaryCTA)}</button>
  // [DISABLED] </form>
  // [DISABLED] <p class="veluna-quiz-abandonment-modal__skip-text" style="margin-top: 12px; text-align: center;">
  // [DISABLED] <button type="button" class="veluna-quiz-abandonment-modal__skip" style="background: none; border: none; color: var(--text-eyebrow, #666); text-decoration: underline; cursor: pointer; font-size: inherit;">${escapeHtml(copy.secondaryLink || 'Continue Quiz')}</button>
  // [DISABLED] </p>
  // [DISABLED] </div>
  // [DISABLED] `;
  // [DISABLED] 
  // [DISABLED] document.body.appendChild(modal);
  // [DISABLED] 
  // [DISABLED] // Focus trap: Get focusable elements
  // [DISABLED] const focusableElements = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  // [DISABLED] const firstFocusable = focusableElements[0];
  // [DISABLED] const lastFocusable = focusableElements[focusableElements.length - 1];
  // [DISABLED] 
  // [DISABLED] // Focus first element
  // [DISABLED] if (firstFocusable) {
  // [DISABLED] firstFocusable.focus();
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] // Close function (with cooldown)
  // [DISABLED] function closeModal() {
  // [DISABLED] setExitModalCooldown(); // Set cooldown when modal closes
  // [DISABLED] modal.remove();
  // [DISABLED] document.body.style.overflow = '';
  // [DISABLED] // Remove ESC listener
  // [DISABLED] document.removeEventListener('keydown', handleEscKey);
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] // ESC key handler
  // [DISABLED] function handleEscKey(e) {
  // [DISABLED] if (e.key === 'Escape' || e.keyCode === 27) {
  // [DISABLED] closeModal();
  // [DISABLED] }
  // [DISABLED] }
  // [DISABLED] document.addEventListener('keydown', handleEscKey);
  // [DISABLED] 
  // [DISABLED] // Close handlers
  // [DISABLED] modal.querySelector('.veluna-quiz-abandonment-modal__close').addEventListener('click', closeModal);
  // [DISABLED] modal.querySelector('.veluna-quiz-abandonment-modal__backdrop').addEventListener('click', closeModal);
  // [DISABLED] 
  // [DISABLED] // Close/continue button (no guest mode - just close modal)
  // [DISABLED] const skipButton = modal.querySelector('.veluna-quiz-abandonment-modal__skip');
  // [DISABLED] if (skipButton) {
  // [DISABLED] skipButton.addEventListener('click', function(e) {
  // [DISABLED] e.preventDefault();
  // [DISABLED] // Just close modal - no guest mode set, user can continue quiz normally
  // [DISABLED] closeModal();
  // [DISABLED] });
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] // Form submission
  // [DISABLED] const form = modal.querySelector('#veluna-quiz-abandonment-form');
  // [DISABLED] if (form) {
  // [DISABLED] form.addEventListener('submit', function(e) {
  // [DISABLED] e.preventDefault();
  // [DISABLED] const emailInput = this.querySelector('input[type="email"]');
  // [DISABLED] const email = emailInput ? emailInput.value.trim() : '';
  // [DISABLED] 
  // [DISABLED] if (email) {
  // [DISABLED] // Set email captured using state module (with guest permanence guard)
  // [DISABLED] let emailSet = false;
  // [DISABLED] if (window.VelunaRitualState && typeof window.VelunaRitualState.setEmailCaptured === 'function') {
  // [DISABLED] emailSet = window.VelunaRitualState.setEmailCaptured(email);
  // [DISABLED] } else {
  // [DISABLED] // Fallback: set flags directly (no guest guard in fallback)
  // [DISABLED] localStorage.setItem('veluna_quiz_email_captured', 'true');
  // [DISABLED] localStorage.setItem('veluna_quiz_email', email);
  // [DISABLED] localStorage.setItem('veluna_quiz_email_timestamp', Date.now().toString());
  // [DISABLED] localStorage.removeItem('veluna_quiz_guest');
  // [DISABLED] emailSet = true;
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] // Email capture always succeeds (no guest permanence guard)
  // [DISABLED] // Also save to legacy key for backward compatibility
  // [DISABLED] localStorage.setItem('veluna_quiz_email', email);
  // [DISABLED] 
  // [DISABLED] // Save progress
  // [DISABLED] saveProgress();
  // [DISABLED] 
  // [DISABLED] // Track analytics event using guarded helper
  // [DISABLED] if (window.VelunaRitualState && typeof window.VelunaRitualState.emitEvent === 'function') {
  // [DISABLED] window.VelunaRitualState.emitEvent('quiz_email_captured', {
  // [DISABLED] event_category: 'Quiz',
  // [DISABLED] event_label: 'exit_intent',
  // [DISABLED] value: currentStep
  // [DISABLED] });
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] closeModal();
  // [DISABLED] }
  // [DISABLED] });
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] // Track modal shown event using guarded helper
  // [DISABLED] if (window.VelunaRitualState && typeof window.VelunaRitualState.emitEvent === 'function') {
  // [DISABLED] window.VelunaRitualState.emitEvent('quiz_exit_modal_shown', {
  // [DISABLED] event_category: 'Quiz',
  // [DISABLED] event_label: 'exit_intent',
  // [DISABLED] value: currentStep
  // [DISABLED] });
  // [DISABLED] }
  // [DISABLED] 
  // [DISABLED] // Set cooldown when modal is shown
  // [DISABLED] setExitModalCooldown();
  // [DISABLED] 
  // [DISABLED] // Prevent body scroll
  // [DISABLED] document.body.style.overflow = 'hidden';
  // [DISABLED] }
  
  // Expose manual initialization function globally
  window.initVelunaQuiz = function() {
    console.log('Quiz: Manual initialization triggered');
    initializeQuiz();
    initQuizAbandonmentRecovery();
  };
  
  // Auto-initialize when DOM is ready - More aggressive initialization
  function startQuiz() {
    console.log('Quiz: Starting initialization sequence...');
    
    try {
      // Try immediate initialization
      initializeQuiz();
      initQuizAbandonmentRecovery();
    } catch (error) {
      console.error('Quiz: Error during initialization:', error);
      // Show fallback question on error
      const fallback = document.querySelector('[data-quiz-fallback-question]');
      if (fallback) {
        fallback.style.display = 'block';
        console.log('Quiz: Showing fallback question due to initialization error');
      }
    }
    
    // Also set up fallback initialization with multiple retries
    let retryCount = 0;
    const maxRetries = 20;
    
    const retryInterval = setInterval(() => {
      retryCount++;
      const container = document.querySelector('[data-quiz-question-container]');
      // Check for rendered content (excluding fallback)
      const fallback = document.querySelector('[data-quiz-fallback-question]');
      const hasContent = container && container.querySelector('h2:not([data-quiz-fallback-question] h2)');
      
      if (!hasContent && retryCount < maxRetries) {
        console.log(`Quiz: Retry ${retryCount} - Questions not visible, re-initializing...`);
        initializeQuiz();
      } else {
        clearInterval(retryInterval);
        if (hasContent) {
          console.log('Quiz: Questions successfully rendered');
          // Hide fallback if questions rendered
          if (fallback) {
            fallback.style.display = 'none';
          }
        } else {
          console.error('Quiz: Failed to render questions after', maxRetries, 'attempts');
          // Show fallback question if it exists and isn't already visible
          if (fallback && fallback.style.display !== 'block') {
            fallback.style.display = 'block';
            console.log('Quiz: Showing fallback question');
          }
        }
      }
    }, 300);
    
    // Clean up interval after max time
    setTimeout(() => clearInterval(retryInterval), maxRetries * 300);
  }
  
  // Wrap initialization in try-catch to prevent complete failure
  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        console.log('Quiz: DOMContentLoaded fired');
        startQuiz();
      });
    } else {
      console.log('Quiz: DOM already loaded');
      startQuiz();
    }
  } catch (error) {
    console.error('Quiz: Fatal error during script initialization:', error);
    // Ensure fallback question is visible
    const fallback = document.querySelector('[data-quiz-fallback-question]');
    if (fallback) {
      fallback.style.display = 'block';
    }
  }
})();

